/**
 * Flight paths (docs/spec/02 §4, §5): a path is drawn from lines, arcs and steers, and kept as a
 * point for every pixel travelled, so an enemy flying it only has to count distance.
 */

import { angleDiff, atan2, clamp, cos, distance, PI, sin, TAU } from './math';

export interface Path {
  readonly xs: Float64Array;
  readonly ys: Float64Array;
  /** Heading at each point. */
  readonly hs: Float64Array;
  /** Pixels from the first point to the last. */
  readonly length: number;
}

export interface PathPoint {
  x: number;
  y: number;
  h: number;
}

/** A steer gives up after this many pixels (a target it can only circle). */
const STEER_LIMIT = 900;

export class PathBuilder {
  private readonly xs: number[] = [];
  private readonly ys: number[] = [];
  private readonly hs: number[] = [];
  x: number;
  y: number;
  /** Heading, radians. */
  h: number;

  constructor(x: number, y: number, heading: number) {
    this.x = x;
    this.y = y;
    this.h = heading;
    this.push();
  }

  private push(): void {
    this.xs.push(this.x);
    this.ys.push(this.y);
    this.hs.push(this.h);
  }

  private advance(step: number): void {
    this.x += cos(this.h) * step;
    this.y += sin(this.h) * step;
    this.push();
  }

  /** Straight on for `length` pixels. */
  line(length: number): this {
    const n = Math.max(1, Math.round(length));
    for (let i = 0; i < n; i++) this.advance(length / n);
    return this;
  }

  /** Turn through `sweep` radians (positive is clockwise on screen) on a circle of `radius`. */
  arc(sweep: number, radius: number): this {
    const length = Math.abs(sweep) * radius;
    const n = Math.max(1, Math.round(length));
    for (let i = 0; i < n; i++) {
      this.h += sweep / n;
      this.advance(length / n);
    }
    return this;
  }

  /** Turn the shorter way round to face `heading`, on a circle of `radius`. */
  turnTo(heading: number, radius: number): this {
    return this.arc(angleDiff(heading, this.h), radius);
  }

  /**
   * Turn toward (tx, ty) no tighter than `radius`, then fly at it, stopping within `stop` pixels
   * (or at the nearest it gets).
   */
  steerTo(tx: number, ty: number, radius: number, stop = 1): this {
    const maxTurn = 1 / Math.max(1, radius);
    let best = distance(this.x, this.y, tx, ty);
    let turned = 0;
    let faced = false;
    for (let i = 0; i < STEER_LIMIT; i++) {
      const d = distance(this.x, this.y, tx, ty);
      if (d <= Math.max(stop, 1)) break;
      const diff = angleDiff(atan2(ty - this.y, tx - this.x), this.h);
      const facing = Math.abs(diff) < PI / 2;
      if (facing) faced = true;
      // It has pointed at the target, the target is now behind and falling away: it has been passed.
      if (faced && !facing && d > best + 2) break;
      if (d < best) {
        best = d;
        turned = 0;
      }
      const turn = clamp(diff, -maxTurn, maxTurn);
      // A whole circle and no nearer: the target is inside the turn and cannot be reached.
      turned += Math.abs(turn);
      if (turned > TAU) break;
      this.h += turn;
      this.advance(1);
    }
    return this;
  }

  /** Straight on until `y` is passed (downward flight off the bottom, say). */
  lineToY(y: number): this {
    const dy = sin(this.h);
    if ((y > this.y && dy > 0.05) || (y < this.y && dy < -0.05)) this.line((y - this.y) / dy);
    return this;
  }

  build(): Path {
    return {
      xs: Float64Array.from(this.xs),
      ys: Float64Array.from(this.ys),
      hs: Float64Array.from(this.hs),
      length: this.xs.length - 1,
    };
  }
}

/** The point `dist` pixels along `path` (held at its ends), written into `out`. */
export function pathPoint(path: Path, dist: number, out: PathPoint): PathPoint {
  const d = clamp(dist, 0, path.length);
  const i = Math.min(path.length - 1, Math.floor(d));
  const f = d - i;
  const x0 = path.xs[i] ?? 0;
  const y0 = path.ys[i] ?? 0;
  out.x = x0 + ((path.xs[i + 1] ?? x0) - x0) * f;
  out.y = y0 + ((path.ys[i + 1] ?? y0) - y0) * f;
  // Headings are not interpolated: a step is one pixel, and they may wrap.
  out.h = path.hs[f < 0.5 ? i : i + 1] ?? 0;
  return out;
}

/** The same path reflected left to right about the screen's centre line (`width / 2`). */
export function mirrorPath(path: Path, width: number): Path {
  return {
    xs: path.xs.map((x) => width - x),
    ys: path.ys,
    hs: path.hs.map((h) => PI - h),
    length: path.length,
  };
}
